import React, { useEffect, useState } from 'react';
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { failureNotification, successNotification } from '@shared/store/notification.duck';
import { ReportType } from '@shared/models/report.models';
import { sendSampleReport } from '@features/settings/services/ReportService';
import { OutlineButton } from "../../atoms/buttons/OutlineButton";
import { ButtonSize } from "../../atoms/buttons/ButtonStyling";
import { BodyVerySmall } from "../../atoms/fonts/Body";
import Colours from "../../atoms/Colours";

interface Props {
  reportType: ReportType;
  /** Sends the sample. Defaults to the per-report-type endpoint; saved reports pass their own. */
  send?: () => Promise<void>;
}

/** Matches the server-side per-report cooldown enforced by SampleReportRateLimiter. */
const COOLDOWN_MS = 15 * 60 * 1000;

const storageKey = (reportType: ReportType) => `sample-report-last-sent:${reportType}`;

const remainingCooldownMs = (reportType: ReportType) => {
  const lastSent = Number(localStorage.getItem(storageKey(reportType)) ?? 0);
  return Math.max(0, lastSent + COOLDOWN_MS - Date.now());
};

/**
 * Sends a one-off sample of the given report to the current user (populated with the previous
 * period's data) so they can preview the format before enabling it. After a send the button is
 * disabled for {@link COOLDOWN_MS}, mirroring the backend rate limit, persisted so a page refresh
 * doesn't reset it.
 */
export function SendSampleReportButton({ reportType, send }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [remainingMs, setRemainingMs] = useState(() => remainingCooldownMs(reportType));
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    setRemainingMs(remainingCooldownMs(reportType));
    const interval = setInterval(() => setRemainingMs(remainingCooldownMs(reportType)), 1000);
    return () => clearInterval(interval);
  }, [reportType]);

  const sendSample = async () => {
    try {
      setIsLoading(true);
      await (send ? send() : sendSampleReport(reportType));
      localStorage.setItem(storageKey(reportType), String(Date.now()));
      setRemainingMs(COOLDOWN_MS);
      dispatch(successNotification(t('settings.reports-sample.success')));
    } catch (e: any) {
      dispatch(failureNotification(t('settings.reports-sample.failure')));
    } finally {
      setIsLoading(false);
    }
  };

  const onCooldown = remainingMs > 0;

  return (
    <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
      <OutlineButton text={'settings.reports-sample.button'}
                     size={ButtonSize.Small}
                     disabled={isLoading || onCooldown}
                     click={sendSample} />
      {onCooldown && <BodyVerySmall colour={Colours.darkGrey} style={{ margin: 0 }}>
        {t('settings.reports-sample.cooldown', { minutes: Math.ceil(remainingMs / 60000) })}
      </BodyVerySmall>}
    </div>
  );
}
